import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { gruposItensApi } from '../../api/endpoints'
import { Button, IconButton } from '../../components/ui/Button'
import { Select } from '../../components/ui/Input'
import { Card, CardHeader } from '../../components/ui/Card'
import { EmptyState, LoadingState } from '../../components/ui/EmptyState'
import { ConfirmDialog, Modal } from '../../components/ui/Modal'
import { Table, THead, Th, Tr, Td } from '../../components/ui/Table'
import { useToast } from '../../components/ui/Toast'
import { PageHeader } from '../../layout/PageHeader'
import { BloqueioPremium } from './BloqueioPremium'
import { GrupoModal } from './GrupoModal'
import { GruposSugeridosModal } from './GruposSugeridosModal'
import { resumoRegras } from './rotulosItens'
import { usePermiteNotas } from './usePermiteNotas'
import type { EssencialOpcional, GrupoItem } from '../../types'

/** Gestão dos grupos de itens (ex.: "Leite em pó"): regras, essencial padrão e acesso ao detalhe por marca. */
export function GruposItensPage() {
  const qc = useQueryClient()
  const toast = useToast()
  const { permite } = usePermiteNotas()
  const [editando, setEditando] = useState<GrupoItem | 'novo' | null>(null)
  const [paraExcluir, setParaExcluir] = useState<GrupoItem | null>(null)
  const [reprocessar, setReprocessar] = useState(false)
  const [sugeridos, setSugeridos] = useState(false)

  const { data: grupos = [], isLoading } = useQuery({ queryKey: ['itens-grupos'], queryFn: gruposItensApi.list, enabled: permite })
  const { data: semGrupo = [] } = useQuery({ queryKey: ['itens-sem-grupo'], queryFn: gruposItensApi.semGrupo, enabled: permite })

  const refaz = () => {
    qc.invalidateQueries({ queryKey: ['itens-grupos'] })
    qc.invalidateQueries({ queryKey: ['itens-sem-grupo'] })
    qc.invalidateQueries({ queryKey: ['itens-grupo-detalhe'] })
    qc.invalidateQueries({ queryKey: ['nota-insights'] })
  }
  const onError = (e: Error) => toast.error(e.message)

  const essencial = useMutation({ mutationFn: (v: { id: string; essencial: EssencialOpcional }) => gruposItensApi.definirEssencial(v.id, v.essencial), onSuccess: refaz, onError })
  const remover = useMutation({
    mutationFn: (id: string) => gruposItensApi.remover(id),
    onSuccess: () => { refaz(); toast.success('Grupo excluído. Os itens dele voltam para a fila sem grupo.'); setParaExcluir(null) },
    onError,
  })
  const reprocessarHistorico = useMutation({
    mutationFn: gruposItensApi.reprocessarHistorico,
    onSuccess: (r) => { refaz(); setReprocessar(false); toast.success(`Histórico agrupado: ${r.agrupados} itens com grupo, ${r.semGrupo} sem grupo.`) },
    onError,
  })

  return (
    <>
      <PageHeader title="Grupos de itens" />
      {!permite ? <BloqueioPremium recurso="Os grupos de itens" /> : (
        <Card>
          <CardHeader
            title="Meus grupos" icon="ri-stack-line"
            actions={<>
              <Link to="/itens-sem-grupo" className="inline-flex h-[38px] items-center gap-2 rounded bg-surface2 px-4 text-13 font-medium text-white hover:bg-surface3">
                <i className="ri-question-line text-base" aria-hidden /> Sem grupo{semGrupo.length > 0 && <span className="rounded bg-expense px-1.5 text-2xs font-semibold text-white">{semGrupo.length}</span>}
              </Link>
              <Button variant="light" icon="ri-magic-line" onClick={() => setSugeridos(true)}>Grupos sugeridos</Button>
              <Button variant="light" icon="ri-refresh-line" onClick={() => setReprocessar(true)}>Agrupar histórico</Button>
              <Button icon="ri-add-line" onClick={() => setEditando('novo')}>Novo grupo</Button>
            </>}
          />
          {isLoading ? <LoadingState /> : grupos.length === 0 ? (
            <EmptyState icon="ri-stack-line" message="Você ainda não criou grupos. Um grupo junta marcas e embalagens do mesmo produto (ex.: Leite em pó) para comparar preços." action={<Button variant="soft-primary" icon="ri-add-line" onClick={() => setEditando('novo')}>Criar o primeiro</Button>} />
          ) : (
            <Table>
              <THead><tr><Th>Grupo</Th><Th>Regras</Th><Th>Padrão</Th><Th className="text-right">Itens</Th><Th className="text-right">Compras</Th><Th className="text-right">Ações</Th></tr></THead>
              <tbody>
                {grupos.map((g) => (
                  <Tr key={g.id}>
                    <Td><Link to={`/grupos-itens/${g.id}`} className="font-medium text-white hover:text-primary">{g.nome}</Link></Td>
                    <Td className="max-w-md text-2xs text-muted">{resumoRegras(g.regras)}</Td>
                    <Td>
                      <div className="w-40">
                        <Select aria-label={`Padrão de ${g.nome}`} value={g.essencial} disabled={essencial.isPending} onChange={(e) => essencial.mutate({ id: g.id, essencial: Number(e.target.value) as EssencialOpcional })}>
                          <option value={0}>Sem classificação</option><option value={1}>Essencial</option><option value={2}>Opcional</option>
                        </Select>
                      </div>
                    </Td>
                    <Td className="text-right text-muted">{g.totalItens}</Td>
                    <Td className="text-right text-muted">{g.totalCompras}</Td>
                    <Td className="whitespace-nowrap text-right">
                      <Link to={`/grupos-itens/${g.id}`} aria-label={`Ver ${g.nome}`} className="inline-flex h-8 w-8 items-center justify-center rounded text-info hover:bg-info/10"><i className="ri-eye-fill" aria-hidden /></Link>
                      <IconButton icon="ri-pencil-fill" label={`Editar ${g.nome}`} tone="info" onClick={() => setEditando(g)} />
                      <IconButton icon="ri-delete-bin-5-fill" label={`Excluir ${g.nome}`} tone="danger" onClick={() => setParaExcluir(g)} />
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      )}

      {sugeridos && permite && <GruposSugeridosModal onClose={() => setSugeridos(false)} onMudou={refaz} />}
      {editando && <GrupoModal grupo={editando === 'novo' ? null : editando} onClose={() => setEditando(null)} />}

      <ConfirmDialog
        open={!!paraExcluir} onClose={() => setParaExcluir(null)} loading={remover.isPending}
        onConfirm={() => paraExcluir && remover.mutate(paraExcluir.id)}
        message={<>O grupo <strong className="text-white">{paraExcluir?.nome}</strong> será excluído. Os itens dele voltam para a fila sem grupo; nenhuma nota é alterada.</>}
      />

      <Modal
        open={reprocessar} onClose={() => setReprocessar(false)} title="Agrupar o histórico" size="sm"
        footer={<><Button variant="light" onClick={() => setReprocessar(false)}>Cancelar</Button><Button icon="ri-refresh-line" loading={reprocessarHistorico.isPending} onClick={() => reprocessarHistorico.mutate()}>Agrupar agora</Button></>}
      >
        <p className="text-13 text-muted">Aplica os grupos e as regras às notas que você já importou. Correções manuais suas são mantidas. Rode quando tiver terminado de ajustar os grupos; pode levar um tempo.</p>
      </Modal>
    </>
  )
}
